// Central company + legal config. Edit the [PLACEHOLDER] values once you have them.
export const COMPANY = {
  legalName: 'GameCore LLP',
  brand: 'DYSSC',
  website: 'https://dyssc.com',
  websiteLabel: 'dyssc.com',
  email: 'orders@dyssc.com',
  ordersEmail: 'orders@dyssc.com',
  phone: '+91 99620 44222',
  whatsapp: '919962044222',
  // ↓↓↓ Fill these in when ready (empty values are omitted from the documents) ↓↓↓
  address: 'New No 22 (Old No 59), 2nd Main Road, R. A. Puram, Chennai 600028, Tamil Nadu, India',
  gstin: '33ABDFG6849F1ZD',
  llpin: 'ACS-2446',
  grievanceOfficer: '',
  grievanceEmail: 'orders@dyssc.com',
  jurisdiction: 'Chennai, Tamil Nadu, India',
  lastUpdated: 'August 21, 2026',
  // Social handles — edit hrefs to your real profiles
  social: {
    instagram: 'https://www.instagram.com/dysscindia',
  },
  instagramHandle: '@dysscindia',
  // Marketplace listings (ASIN B0HJDFHV2Y)
  amazon: {
    hexcore: 'https://www.amazon.in/dp/B0HJDFHV2Y',
  },
};

const C = COMPANY;

// Each document: { slug, title, summary, sections: [{ id, heading, content: [...] }] }
// content item types: string (paragraph) | { ul: [..] } | { h3: 'text' }
export const LEGAL_DOCS = {
  privacy: {
    slug: 'privacy',
    title: 'Privacy Policy',
    summary:
      `This Privacy Policy explains how ${C.legalName} ("${C.brand}", "we", "us") collects, uses, shares and protects your information when you visit ${C.websiteLabel} or buy our products.`,
    sections: [
      {
        id: 'introduction',
        heading: '1. Introduction',
        content: [
          `${C.brand} is a brand operated by ${C.legalName}. We are committed to protecting your privacy and handling your personal data responsibly and in accordance with applicable Indian laws, including the Information Technology Act, 2000 and the Digital Personal Data Protection Act, 2023 (DPDP Act).`,
          `By using our website or purchasing our products, you agree to the practices described in this Policy.`,
        ],
      },
      {
        id: 'information-we-collect',
        heading: '2. Information We Collect',
        content: [
          'We collect the following categories of information:',
          {
            ul: [
              'Identity & contact data: name, email address, phone/WhatsApp number, and shipping/billing address.',
              'Business data: GSTIN (only if you choose to provide it for a business invoice).',
              'Order & transaction data: products ordered, order value, invoices, and payment status. Card/UPI/bank details are processed directly by our payment gateway (Razorpay) — we do not store your full payment credentials.',
              'Communications: messages you send us via the website chat, WhatsApp, email or contact forms.',
              'Technical & usage data: device type, browser, IP address, pages viewed, and cookie identifiers (see our Cookie section).',
            ],
          },
        ],
      },
      {
        id: 'how-we-use',
        heading: '3. How We Use Your Information',
        content: [
          'We use your information to:',
          {
            ul: [
              'Process and deliver your orders, and send order, shipping and invoice notifications via email and WhatsApp.',
              'Provide customer support and respond to your enquiries.',
              'Process payments securely and prevent fraud.',
              'Improve our products, website and services.',
              'Send marketing communications (e.g. drops, restocks, offers) where you have opted in — you can opt out at any time.',
              'Comply with legal, tax and regulatory obligations.',
            ],
          },
        ],
      },
      {
        id: 'cookies',
        heading: '4. Cookies & Tracking',
        content: [
          'We use cookies and similar technologies to keep the site working (essential), understand how it is used (analytics) and to support marketing. You control non-essential cookies through our Cookie Preferences centre, which you can open any time from the link in the footer.',
        ],
      },
      {
        id: 'sharing',
        heading: '5. How We Share Your Information',
        content: [
          'We never sell your personal data. We share it only with trusted service providers who help us run our business, under appropriate confidentiality and data-protection terms:',
          {
            ul: [
              'Payments: Razorpay (payment processing).',
              'Email: Resend (transactional and marketing emails).',
              'Messaging: Meta WhatsApp Business / Cloud API (order and support messages).',
              'Logistics: courier and shipping partners (to deliver your order).',
              'Where required by law, court order, or to protect our rights and the safety of others.',
            ],
          },
        ],
      },
      {
        id: 'security',
        heading: '6. Data Security',
        content: [
          'We use industry-standard safeguards — including HTTPS encryption in transit, access controls and secure, reputable infrastructure providers — to protect your information. No method of transmission or storage is completely secure, but we work continuously to protect your data.',
        ],
      },
      {
        id: 'retention',
        heading: '7. Data Retention',
        content: [
          'We retain personal data for as long as necessary to fulfil the purposes described in this Policy, including to meet accounting, tax, warranty and legal requirements. When data is no longer needed, we delete or anonymise it.',
        ],
      },
      {
        id: 'your-rights',
        heading: '8. Your Rights',
        content: [
          'Subject to applicable law, you may request to:',
          {
            ul: [
              'Access the personal data we hold about you.',
              'Correct or update inaccurate or incomplete data.',
              'Request deletion of your data.',
              'Withdraw consent for marketing or other consent-based processing.',
              'Raise a grievance about how your data is handled.',
            ],
          },
          `To exercise any of these rights, contact us at ${C.grievanceEmail}.`,
        ],
      },
      {
        id: 'childrens-privacy',
        heading: "9. Children's Privacy",
        content: [
          'Our website and products are intended for adults. We do not knowingly collect personal data from children. If you believe a child has provided us data, please contact us and we will remove it.',
        ],
      },
      {
        id: 'changes',
        heading: '10. Changes to This Policy',
        content: [
          'We may update this Policy from time to time. The latest version will always be available on this page with the "Last updated" date below.',
        ],
      },
      {
        id: 'contact',
        heading: '11. Contact Us',
        content: [
          `${C.legalName}`,
          `Email: ${C.email}`,
          `Phone / WhatsApp: ${C.phone}`,
          `Registered office: ${C.address}`,
          ...(C.gstin ? [`GSTIN: ${C.gstin}`] : []),
          ...(C.llpin ? [`LLPIN: ${C.llpin}`] : []),
        ],
      },
    ],
  },

  'consumer-data-policy': {
    slug: 'consumer-data-policy',
    title: 'Consumer Data Policy',
    summary:
      `This Consumer Data Policy describes how ${C.legalName} processes the personal data of consumers ("Data Principals") in line with the Digital Personal Data Protection Act, 2023 (DPDP Act).`,
    sections: [
      {
        id: 'overview',
        heading: '1. Overview & Scope',
        content: [
          `${C.legalName} acts as a "Data Fiduciary" in respect of the personal data we collect from our consumers. This Policy supplements our Privacy Policy and focuses on your rights and our obligations under the DPDP Act, 2023.`,
        ],
      },
      {
        id: 'lawful-basis',
        heading: '2. Lawful Basis & Consent',
        content: [
          'We process personal data on the basis of your consent and for legitimate uses permitted under the DPDP Act (such as fulfilling an order you have placed). Where we rely on consent, it is sought through clear, specific and informed requests, and you may withdraw it at any time.',
        ],
      },
      {
        id: 'categories',
        heading: '3. Categories of Personal Data',
        content: [
          {
            ul: [
              'Contact details (name, email, phone/WhatsApp).',
              'Delivery and billing address, and GSTIN (if provided).',
              'Order, invoice and payment-status records.',
              'Support conversations and preferences.',
            ],
          },
        ],
      },
      {
        id: 'purpose',
        heading: '4. Purpose of Processing',
        content: [
          'We process your data only for specified, lawful purposes: order fulfilment and delivery, payments, customer support, statutory compliance (tax/accounting), product improvement, and — with your consent — marketing.',
        ],
      },
      {
        id: 'data-principal-rights',
        heading: '5. Your Rights as a Data Principal',
        content: [
          'Under the DPDP Act you have the right to:',
          {
            ul: [
              'Obtain a summary of the personal data we process and the processing activities.',
              'Request correction, completion, updating or erasure of your personal data.',
              'Nominate another individual to exercise your rights in the event of death or incapacity.',
              'Readily access a grievance-redressal mechanism.',
            ],
          },
        ],
      },
      {
        id: 'withdraw-consent',
        heading: '6. Withdrawing Consent',
        content: [
          `You may withdraw your consent at any time by writing to ${C.grievanceEmail} or using the unsubscribe link in our marketing emails. Withdrawal will not affect processing carried out before withdrawal, or processing required by law.`,
        ],
      },
      {
        id: 'duties',
        heading: '7. Duties of the Data Principal',
        content: [
          'When dealing with us, you agree to provide accurate information and not to impersonate another person or suppress material information where mandatory.',
        ],
      },
      {
        id: 'retention-erasure',
        heading: '8. Retention & Erasure',
        content: [
          'We retain personal data only for as long as necessary for the stated purposes or as required by law, after which it is erased or anonymised.',
        ],
      },
      {
        id: 'grievance',
        heading: '9. Grievance Redressal',
        content: [
          'If you have concerns about how your personal data is processed, you may contact our Grievance Officer:',
          ...(C.grievanceOfficer ? [`Grievance Officer: ${C.grievanceOfficer}`] : [`Grievance desk: ${C.legalName}`]),
          `Email: ${C.grievanceEmail}`,
          `Address: ${C.address}`,
          'We aim to acknowledge and address grievances within the timelines prescribed under applicable law.',
        ],
      },
      {
        id: 'updates',
        heading: '10. Updates',
        content: [
          'We may revise this Consumer Data Policy as our practices or the law evolve. The current version is always published on this page.',
        ],
      },
    ],
  },

  terms: {
    slug: 'terms',
    title: 'Terms of Service',
    summary:
      `These Terms of Service govern your use of ${C.websiteLabel} and your purchase of ${C.brand} products from ${C.legalName}. Please read them carefully.`,
    sections: [
      {
        id: 'acceptance',
        heading: '1. Acceptance of Terms',
        content: [
          `By accessing ${C.websiteLabel} or placing an order, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree, please do not use the website.`,
        ],
      },
      {
        id: 'eligibility',
        heading: '2. Eligibility',
        content: [
          'You must be at least 18 years old (or have the consent of a parent/guardian) and capable of forming a legally binding contract to purchase from us.',
        ],
      },
      {
        id: 'products',
        heading: '3. Products & Descriptions',
        content: [
          'We strive to describe and display our paddles accurately. Colours and finishes may vary slightly due to photography and screen settings. Specifications are subject to manufacturing tolerances.',
        ],
      },
      {
        id: 'pricing',
        heading: '4. Pricing & Taxes',
        content: [
          'All prices are listed in Indian Rupees (INR) and are inclusive of applicable GST unless stated otherwise. We may change prices at any time, but changes will not affect orders already confirmed.',
        ],
      },
      {
        id: 'orders',
        heading: '5. Orders & Acceptance',
        content: [
          'Your order is an offer to buy. We confirm acceptance when we send your order confirmation. We reserve the right to refuse or cancel any order (for example, due to stock, pricing errors, or suspected fraud), in which case any amount paid will be refunded.',
        ],
      },
      {
        id: 'payments',
        heading: '6. Payments',
        content: [
          'Payments are processed securely through Razorpay. By paying, you authorise us (via Razorpay) to charge the applicable amount. We do not store your full card/UPI/bank details.',
        ],
      },
      {
        id: 'shipping',
        heading: '7. Shipping & Delivery',
        content: [
          'We ship pan-India and typically dispatch within 1–2 working days. Estimated delivery is 2–3 business days for metros and 4–6 business days for other locations. See our Shipping & Returns page for full details.',
        ],
      },
      {
        id: 'returns',
        heading: '8. Returns, Refunds & Cancellations',
        content: [
          'Returns are accepted only in case of verified manufacturing defects, for unused paddles in original condition and packaging. To initiate a return, send clear photos of the defect to our team on WhatsApp for review; once approved, we will guide you through the next steps. Refunds or replacements are processed after the returned item is received and inspected. Full details are on our Shipping & Returns page.',
        ],
      },
      {
        id: 'warranty',
        heading: '9. Warranty',
        content: [
          'Every DYSSC paddle is covered by a 6-month manufacturer warranty against manufacturing defects. The warranty does not cover normal wear and tear, misuse, or accidental damage.',
        ],
      },
      {
        id: 'ip',
        heading: '10. Intellectual Property',
        content: [
          `All content on ${C.websiteLabel} — including logos, designs, text, graphics and images — is the property of ${C.legalName} or its licensors and is protected by intellectual-property laws. You may not copy, reproduce or use it without our written permission.`,
        ],
      },
      {
        id: 'acceptable-use',
        heading: '11. Acceptable Use',
        content: [
          'You agree not to misuse the website, interfere with its operation, attempt unauthorised access, or use it for any unlawful purpose.',
        ],
      },
      {
        id: 'liability',
        heading: '12. Limitation of Liability',
        content: [
          'To the maximum extent permitted by law, our total liability for any claim arising from your purchase is limited to the amount you paid for the relevant product. We are not liable for indirect or consequential losses.',
        ],
      },
      {
        id: 'governing-law',
        heading: '13. Governing Law & Jurisdiction',
        content: [
          `These Terms are governed by the laws of India. Any disputes are subject to the exclusive jurisdiction of the courts at ${C.jurisdiction}.`,
        ],
      },
      {
        id: 'changes',
        heading: '14. Changes to These Terms',
        content: [
          'We may update these Terms from time to time. Continued use of the website after changes constitutes acceptance of the revised Terms.',
        ],
      },
      {
        id: 'contact',
        heading: '15. Contact Us',
        content: [
          `${C.legalName}`,
          `Email: ${C.email}`,
          `Phone / WhatsApp: ${C.phone}`,
          `Registered office: ${C.address}`,
          ...(C.llpin ? [`LLPIN: ${C.llpin}`] : []),
          ...(C.gstin ? [`GSTIN: ${C.gstin}`] : []),
        ],
      },
    ],
  },

  'shipping-returns': {
    slug: 'shipping-returns',
    title: 'Shipping & Returns',
    summary:
      'Everything you need to know about how we dispatch your DYSSC paddle, delivery timelines, and our returns, refunds and cancellation process.',
    sections: [
      {
        id: 'processing',
        heading: '1. Order Processing',
        content: [
          'Orders are processed and dispatched within 1–2 working days of payment confirmation. You will receive an order confirmation and shipping update via email and WhatsApp.',
        ],
      },
      {
        id: 'timelines',
        heading: '2. Delivery Timelines',
        content: [
          {
            ul: [
              'Metro cities: typically 2–3 business days after dispatch.',
              'Other locations: typically 4–6 business days after dispatch.',
              'Timelines are estimates and may vary due to courier and location factors.',
            ],
          },
        ],
      },
      {
        id: 'charges',
        heading: '3. Shipping Charges',
        content: [
          'Any applicable shipping charges are shown at checkout before you pay. We ship across India.',
        ],
      },
      {
        id: 'tracking',
        heading: '4. Order Tracking',
        content: [
          'Once your order ships, we share tracking details over WhatsApp and email so you can follow your delivery.',
        ],
      },
      {
        id: 'returns-eligibility',
        heading: '5. Returns Eligibility',
        content: [
          'We accept returns only in case of manufacturing defects, for paddles that are:',
          {
            ul: [
              'Unused and in original, resalable condition.',
              'In their original packaging with all tags and accessories.',
            ],
          },
          'For hygiene and safety, used paddles cannot be returned unless they are defective.',
        ],
      },
      {
        id: 'how-to-return',
        heading: '6. How to Initiate a Return',
        content: [
          `To initiate a return, send clear photos of the manufacturing defect to our team on WhatsApp at ${C.phone} (or email ${C.email}) along with your order number. Once we review and approve the defect, our team will guide you through the next steps.`,
        ],
      },
      {
        id: 'refunds',
        heading: '7. Refunds',
        content: [
          'Once your defect return is approved and we receive and inspect the paddle, your refund or replacement is processed. Refunds are issued to the original payment method via Razorpay and typically reflect within 5–7 business days, depending on your bank.',
        ],
      },
      {
        id: 'cancellations',
        heading: '8. Cancellations',
        content: [
          'You can request to cancel an order before it is dispatched by contacting us as soon as possible. Once an order has shipped, it follows the standard returns process.',
        ],
      },
      {
        id: 'damaged-defective',
        heading: '9. Damaged or Defective Items',
        content: [
          'If your paddle arrives damaged or develops a manufacturing defect within the 6-month warranty period, contact us with photos and your order number and we will arrange a replacement or resolution.',
        ],
      },
      {
        id: 'contact',
        heading: '10. Need Help?',
        content: [
          `Email: ${C.email}`,
          `WhatsApp / Phone: ${C.phone}`,
          'Support hours: Mon–Fri, 9 AM – 6 PM IST.',
        ],
      },
    ],
  },

  'data-deletion': {
    slug: 'data-deletion',
    title: 'Data Deletion Request',
    summary:
      `${C.brand} (operated by ${C.legalName}) respects your right to control your personal data. You can request deletion of your account and associated personal data at any time.`,
    sections: [
      {
        id: 'how-to-request',
        heading: '1. How to Request Deletion',
        content: [
          'Send a deletion request through either channel:',
          {
            ul: [
              `Email: ${C.email} with the subject line "Data Deletion Request", from the email address registered on your account.`,
              'WhatsApp: Message our business number +91 99620 44222 with the words "Delete my data" from your registered mobile number.',
            ],
          },
        ],
      },
      {
        id: 'what-we-delete',
        heading: '2. What We Delete',
        content: [
          'Upon a verified request, we permanently delete your account, profile details (name, email, phone number, addresses), order history associations, and WhatsApp communication history within 30 days of verification.',
        ],
      },
      {
        id: 'what-we-retain',
        heading: '3. What We Retain',
        content: [
          'We retain tax invoices and transaction records as required under Indian law (including GST regulations), and records needed to resolve disputes or enforce our Terms of Service. Retained records are kept only as long as legally required and are not used for marketing.',
        ],
      },
      {
        id: 'confirmation',
        heading: '4. Confirmation',
        content: [
          `We will confirm your identity before processing and send written confirmation once deletion is complete. Questions about this process: ${C.email}.`,
        ],
      },
    ],
  },
};

export const FOOTER_LEGAL_LINKS = [
  { label: 'Privacy Policy', to: '/privacy' },
  { label: 'Consumer Data Policy', to: '/consumer-data-policy' },
  { label: 'Terms of Service', to: '/terms' },
  { label: 'Shipping & Returns', to: '/shipping-returns' },
  { label: 'Data Deletion', to: '/data-deletion' },
];

// Quick FAQ for the Get-in-Touch section
export const CONTACT_FAQS = [
  {
    q: 'How long does shipping take?',
    a: 'We dispatch within 1–2 working days. Metro deliveries usually arrive in 2–3 business days, and other locations in 4–6 business days. You get tracking on WhatsApp.',
  },
  {
    q: 'What is your return policy?',
    a: 'Returns are accepted only for paddles with verified manufacturing defects. To initiate a return, please send clear photos of the issue to our team via WhatsApp for review. Once approved, our team will guide you through the next steps.',
  },
  {
    q: 'Is there a warranty?',
    a: 'Yes — every DYSSC paddle comes with a 6-month manufacturer warranty against manufacturing defects.',
  },
  {
    q: 'Which payment methods do you accept?',
    a: 'We accept cards, UPI, net-banking and wallets securely through Razorpay. All prices are in INR and inclusive of GST.',
  },
  {
    q: 'Are DYSSC paddles USA Pickleball approved?',
    a: 'Yes. The DYSSC HexCore Pro is USA Pickleball (USAPA) approved and built with Toray T700 carbon fibre.',
  },
  {
    q: 'Can I get a GST invoice for my business?',
    a: 'Absolutely. Add your GSTIN at checkout and we will issue a GST invoice for Input Tax Credit.',
  },
];
